// utils/AudioManager.js
class AudioManager {
  constructor() {
    // ---- HTMLAudio 共有要素（フォールバック）----
    this.audioEl = null;
    this.unlocked = false;

    // ---- Web Audio ----
    this.ctx = null;
    this.gain = null;
    this.webUnlocked = false;

    // 状態
    this.isPlaying = false;
    this.currentSource = null;

    // ★ 追加：再生終了（自然終了・明示停止どちらも）を通知するコールバック
    this.onEndedCallback = null;

    // キャッシュ
    this.bufferCache = new Map();   // url -> { ctxId, buffer }
    this.rawCache = new Map();      // url -> ArrayBuffer（許可前にfetchだけ）
  }

  // ★ 追加：再生終了を1回だけ通知する内部ヘルパー
  // WebAudioの自然終了(onended)・HTMLAudioの自然終了(ended)・明示的なstop()の
  // いずれからも呼ばれる可能性があるが、呼び出し後すぐコールバックをクリアするため
  // 二重に呼ばれても外部へは1回しか通知されない
  _notifyEnded() {
    this.isPlaying = false;
    this.currentSource = null;
    const cb = this.onEndedCallback;
    this.onEndedCallback = null;
    if (cb) {
      try {
        cb();
      } catch (e) {
        console.error('AudioManager onEnded callback error:', e);
      }
    }
  }

  /** 許可タップ中: 共有 <audio> を play→pause でプライム */
  async primeHtmlAudio() {
    if (this.unlocked && this.audioEl) return true;
    this.audioEl = this.audioEl || new Audio();
    try {
      // 空srcでも gesture 中の play が重要
      this.audioEl.src = '';
      this.audioEl.load();
      try { await this.audioEl.play(); } catch {}
      this.audioEl.pause();
      this.audioEl.currentTime = 0;
      this.unlocked = true;
      return true;
    } catch (e) {
      console.warn('primeHtmlAudio failed', e);
      this.unlocked = false;
      return false;
    }
  }

  /** 外部の AudioContext を共有。ctx が変わったらノード作り直し */
  setExternalContext(ctx) {
    if (!ctx) return;
    // ctx が変わるなら古いノードを破棄
    if (this.ctx && this.ctx !== ctx) {
      try { this.gain?.disconnect(); } catch {}
      this.gain = null;
      // currentSource はコンテキスト越境不可なので止める
      if (this.currentSource) {
        try { this.currentSource.stop(0); } catch {}
        this.currentSource = null;
      }
    }
    this.ctx = ctx;
    // 新しい ctx で GainNode を用意
    if (!this.gain) {
      this.gain = this.ctx.createGain();
      this.gain.connect(this.ctx.destination);
    }
    this.webUnlocked = true;
  }

  /** 先読み（許可前はfetchだけ。デコードは再生時のctxで） */
  async preload(url) {
    if (!url) return;
    if (this.bufferCache.has(url)) return; // 既にデコード済み
    if (this.rawCache.has(url)) return;    // 既にfetch済み
    const res = await fetch(url);
    const arr = await res.arrayBuffer();
    this.rawCache.set(url, arr);
  }

  /**
   * 再生：WebAudioを優先、なければHTMLAudioフォールバック
   * ★ 第二引数で onEnded コールバックを登録できる（自然終了・stop両方で呼ばれる）
   */
  async play(audioPath, { onEnded } = {}) {
    try {
      this.stop(); // 前の再生があれば止める（そのonEndedも発火する）

      if (!audioPath || !audioPath.trim()) {
        console.warn('Audio path is empty or invalid:', audioPath);
        return false;
      }

      // ★ 今回の再生用にコールバックを登録
      this.onEndedCallback = onEnded || null;

      // ===== WebAudio 経路 =====
      if (this.ctx && this.webUnlocked) {
        try { if (this.ctx.state === 'suspended') await this.ctx.resume(); } catch {}

        // 現在の ctx ID（オブジェクト参照を文字化）
        const ctxId = String(this.ctx);

        // 1) デコード済みが現在ctx用にあればそれを使う
        let bufferEntry = this.bufferCache.get(audioPath);
        if (!bufferEntry || bufferEntry.ctxId !== ctxId) {
          // 2) 生データがあればそれを現在ctxでデコード、なければfetch
          let arr = this.rawCache.get(audioPath);
          if (!arr) {
            const res = await fetch(audioPath);
            arr = await res.arrayBuffer();
            this.rawCache.set(audioPath, arr);
          }
          const buf = await this.ctx.decodeAudioData(arr.slice(0)); // sliceでdetach
          bufferEntry = { ctxId, buffer: buf };
          this.bufferCache.set(audioPath, bufferEntry);
        }

        // 3) 同じ ctx でノード作成＆接続
        const src = this.ctx.createBufferSource();
        src.buffer = bufferEntry.buffer;

        // Safety: gain が別ctxのものになっていないか確認
        if (!this.gain || this.gain.context !== this.ctx) {
          // 念のため再生成
          try { this.gain?.disconnect(); } catch {}
          this.gain = this.ctx.createGain();
          this.gain.connect(this.ctx.destination);
        }

        src.connect(this.gain);
        src.start(0);
        this.currentSource = src;
        this.isPlaying = true;
        // ★ 自然終了・stop()経由の停止、どちらでも発火する（stop()はsrc.stop(0)を呼ぶため）
        src.onended = () => { this._notifyEnded(); };
        return true;
      }

      // ===== HTMLAudio フォールバック =====
      if (!this.audioEl) this.audioEl = new Audio();
      if (!this.unlocked) {
        console.warn('HTMLAudio not primed. Call primeHtmlAudio() in a user gesture.');
      }

      this.audioEl.src = audioPath;
      this.audioEl.load();

      return await new Promise((resolve, reject) => {
        const onCanPlay = async () => {
          try {
            const p = this.audioEl.play();
            if (p && typeof p.then === 'function') await p;
            this.isPlaying = true;
            resolve(true);
          } catch (err) {
            console.error('HTMLAudio play blocked:', err);
            this.isPlaying = false;
            resolve(false);
          }
        };
        // ★ 自然終了時。stop()による明示停止はpause()のみでこのイベントは発火しないため、
        //   stop()側で別途 _notifyEnded() を呼んでカバーする
        const onEndedEvt = () => { this._notifyEnded(); };
        const onError = () => {
          this._notifyEnded();
          reject(new Error('Audio loading error'));
        };

        this.audioEl.addEventListener('canplay', onCanPlay, { once: true });
        this.audioEl.addEventListener('canplaythrough', onCanPlay, { once: true });
        this.audioEl.addEventListener('ended', onEndedEvt);
        this.audioEl.addEventListener('error', onError, { once: true });

        const to = setTimeout(() => {
          this.audioEl.removeEventListener('canplay', onCanPlay);
          this.audioEl.removeEventListener('canplaythrough', onCanPlay);
          this.audioEl.removeEventListener('ended', onEndedEvt);
          this.audioEl.removeEventListener('error', onError);
          this.stop();
          reject(new Error(`Audio loading timeout: ${audioPath}`));
        }, 5000);

        this.audioEl.addEventListener('loadeddata', () => clearTimeout(to), { once: true });
      });

    } catch (error) {
      console.error('Error in AudioManager.play:', error);
      this._notifyEnded();
      return false;
    }
  }

  async playSimple(audioPath, opts) {
    return this.play(audioPath, opts);
  }

  stop() {
    if (this.currentSource) {
      try { this.currentSource.stop(0); } catch {}
      try { this.currentSource.disconnect(); } catch {}
      this.currentSource = null;
    }
    if (this.audioEl) {
      try {
        this.audioEl.pause();
        this.audioEl.currentTime = 0;
      } catch {}
    }
    // ★ 明示停止でも必ずコールバックを発火させる
    //   （WebAudioはsrc.onendedでも発火するが、_notifyEndedは二重発火してもコールバックは1回だけ実行される）
    this._notifyEnded();
  }

  getPlayingStatus() {
    return {
      isPlaying: this.isPlaying,
      usingWebAudio: !!this.ctx && this.webUnlocked,
      unlockedHtmlAudio: this.unlocked
    };
  }
}

const audioManager = new AudioManager();
export default audioManager;